import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import type { ServerCostSummary } from '../api/types.js';

interface CostChartProps {
  servers: ServerCostSummary[];
  shared: { appRunner: number; apiGateway: number; total: number };
  onBarClick?: (serverId: string) => void;
}

function formatUsd(value: number): string {
  return `$${value.toFixed(2)}`;
}

interface ChartEntry {
  name: string;
  cost: number;
  serverId?: string;
}

export function CostChart({ servers, shared, onBarClick }: CostChartProps) {
  const data: ChartEntry[] = [
    ...servers.map((s) => ({ name: s.organization || s.serverId, cost: s.total, serverId: s.serverId })),
  ];

  if (shared.total > 0) {
    data.push({ name: 'Shared infra', cost: shared.total });
  }

  if (data.length === 0 || data.every((d) => d.cost === 0)) {
    return (
      <div className="flex h-64 items-center justify-center" style={{ color: 'var(--color-text-3)' }}>
        No cost data for this period.
      </div>
    );
  }

  return (
    <ResponsiveContainer width="100%" height={320}>
      <BarChart data={data} margin={{ top: 4, right: 4, left: 4, bottom: 4 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
        <XAxis dataKey="name" tick={{ fontSize: 12, fill: 'var(--color-text-3)' }} />
        <YAxis tickFormatter={formatUsd} tick={{ fontSize: 12, fill: 'var(--color-text-3)' }} />
        <Tooltip
          formatter={(value) => [formatUsd(Number(value ?? 0)), 'Cost']}
          contentStyle={{ backgroundColor: 'var(--color-surface-2)', border: '1px solid var(--color-border)', borderRadius: 6, color: 'var(--color-text)' }}
          labelStyle={{ color: 'var(--color-text-2)' }}
        />
        <Legend wrapperStyle={{ color: 'var(--color-text-2)', fontSize: 12 }} />
        <Bar
          dataKey="cost"
          fill="var(--color-brand)"
          radius={[4, 4, 0, 0]}
          name="AWS Cost"
          cursor={onBarClick ? 'pointer' : undefined}
          onClick={(_data: unknown, index: number) => {
            if (onBarClick && data[index]?.serverId) onBarClick(data[index].serverId!);
          }}
        />
      </BarChart>
    </ResponsiveContainer>
  );
}
